import {
    CircleIcon,
    Icon,
    Pane,
    TickCircleIcon,
    CaretDownIcon,
    CaretRightIcon,
    LockIcon,
    toaster,
    Text,
} from 'evergreen-ui';
import { ProgressDonut } from '../../../components';
import { history } from 'cademy-shared-web';
import styles from '../CourseViewer.module.css';
import { ReactComponent as CalendarIcon } from '../../../assets/icons/calendar-day-light.svg';
import { renderDateTimes } from '../../../services/renderOccDateTimes';
import { useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { LessonScheduleBadge } from './LessonScheduleBadge';
import { LessonDeadlineBadge } from './LessonDeadlineBadge';

const SectionProgress = ({ section, lessonProgress = [], currentStudentId }) => {
    const progress = useMemo(() => {
        const sectionLessonRefs = section.contents
            .filter((content) => content.type === 'lesson')
            .map((content) => content.ref);
        const completedLessons = sectionLessonRefs.filter((lessonRef) =>
            lessonProgress.find(
                (lp) =>
                    lp.lesson_ref === lessonRef &&
                    lp.student_ref === currentStudentId &&
                    lp.completed_at
            )
        );

        return {
            totalLessons: sectionLessonRefs.length,
            completedLessons: completedLessons.length,
            percentage:
                sectionLessonRefs.length === 0
                    ? 0
                    : Math.round(
                          (completedLessons.length / sectionLessonRefs.length) * 100,
                      ),
        };
    }, [section, lessonProgress, currentStudentId]);

    return (
        <div className={styles.lessons_list}>
            <ProgressDonut percentage={progress.percentage} />
        </div>
    );
};

const Section = ({
    defaultExpanded = false,
    sectionName,
    section,
    lessonProgress,
    currentStudentId,
    children,
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    return (
        <div>
            <div onClick={() => setExpanded(!expanded)} className={styles.section_summary}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    {expanded ? <CaretDownIcon size={20} /> : <CaretRightIcon size={20} />}
                    <h4>{sectionName}</h4>
                </div>
                {section && (
                    <SectionProgress
                        section={section}
                        lessonProgress={lessonProgress}
                        currentStudentId={currentStudentId}
                    />
                )}
            </div>
            {expanded ? children : null}
        </div>
    );
};

const CourseSections = ({
    course,
    lessons,
    occurrence,
    lessonProgress,
    selectedLesson,
    setSelectedLesson,
    courseCompletePercentage,
    record,
    completed,
    currentStudentId,
}) => {
    const getLockMessage = useCallback(
        (lesson) => {
            if (!lesson.schedule) {
                return 'This lesson is locked. Please complete the previous lesson first.';
            }

            const now = new Date();

            if (lesson.schedule.type === 'relative' && record?.created_at) {
                const availableDate = new Date(record.created_at * 1000);
                availableDate.setDate(
                    availableDate.getDate() + lesson.schedule.daysAfterEnrollment
                );
                const daysUntilAvailable = Math.ceil(
                    (availableDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
                );
                return `This lesson will become available in ${daysUntilAvailable} ${daysUntilAvailable === 1 ? 'day' : 'days'}`;
            }

            if (lesson.schedule.type === 'fixed') {
                const date = new Date(lesson.schedule.timestamp * 1000).toLocaleDateString(
                    undefined,
                    {
                        day: '2-digit',
                        month: '2-digit',
                        year: '2-digit',
                    }
                );
                return `This lesson will become available on ${date}`;
            }

            return 'This lesson is locked. Please complete the previous lesson first.';
        },
        [record?.created_at]
    );

    const renderLessonLink = (section) => {
        return section.contents.map((content) => {
            if (content.type === 'lesson') {
                const lesson = lessons.find((lesson) => lesson._id === content.ref);
                if (!lesson) return null;

                const progress = (lessonProgress || []).find(
                    (progress) =>
                        progress.lesson_ref === lesson._id &&
                        progress.student_ref === currentStudentId
                );

                return (
                    <Link
                        key={content._id}
                        className={
                            selectedLesson && content.ref === selectedLesson._id
                                ? styles.content_link_selected + ' ' + styles.content_link
                                : styles.content_link
                        }
                        to={lesson.unlocked ? `?lesson=${content.ref}` : '#'}
                        onClick={() => {
                            if (lesson.unlocked) {
                                setSelectedLesson(lesson);
                            } else {
                                toaster.notify(getLockMessage(lesson), { duration: 3 });
                            }
                        }}
                    >
                        <Pane
                            style={{ marginLeft: '5px' }}
                            display="flex"
                            alignItems="start"
                            gap={5}
                        >
                            <Icon
                                icon={
                                    lesson.unlocked
                                        ? progress?.completed_at
                                            ? TickCircleIcon
                                            : CircleIcon
                                        : LockIcon
                                }
                                color={
                                    progress?.completed_at ? 'var(--green)' : 'var(--text-muted)'
                                }
                                size={16}
                                marginRight={10}
                                marginTop={2}
                            />
                            <Pane display="flex" flexDirection="column" gap={5}>
                                <Text>{lesson.name}</Text>
                                <LessonScheduleBadge
                                    schedule={lesson.schedule}
                                    enrollmentDate={record?.created_at}
                                />
                                <LessonDeadlineBadge
                                    deadline={lesson.deadline}
                                    enrollmentDate={record?.created_at}
                                />
                            </Pane>
                        </Pane>
                    </Link>
                );
            }

            return null;
        });
    };

    const renderOccurrenceSection = () => {
        if (!occurrence) return null;

        const occurrenceParam =
            new URLSearchParams(history.location.search).get('occurrence') || '';

        return (
            <>
                <Section
                    defaultExpanded={true}
                    sectionName={
                        <span style={{ padding: '10px 0px', display: 'block' }}>Course Dates</span>
                    }
                    lessonProgress={lessonProgress}
                    currentStudentId={currentStudentId}
                >
                    <div className={styles.lessons_list}>
                        <Link
                            key={occurrence._id}
                            className={
                                occurrenceParam === occurrence._id
                                    ? styles.content_link_selected + ' ' + styles.content_link
                                    : styles.content_link
                            }
                            to={`?occurrence=${occurrence._id}`}
                            onClick={() => {
                                setSelectedLesson('');
                                history.push(`?occurrence=${occurrence._id}`);
                            }}
                        >
                            <CalendarIcon
                                height="16px"
                                color="var(--text-muted)"
                                style={{ marginRight: '5px' }}
                            />
                            <span style={{ marginLeft: '5px' }}>{renderDateTimes(occurrence)}</span>
                        </Link>
                    </div>
                </Section>
            </>
        );
    };

    return (
        <Pane>
            <Pane display="flex" alignItems="center" justifyContent="space-between" padding="10px">
                <h3 style={{ marginLeft: '10px' }}>Course Progress</h3>
                <ProgressDonut percentage={courseCompletePercentage} />
            </Pane>
            {occurrence ? renderOccurrenceSection() : null}
            {course.sections.map((section, index) => {
                if (!section.contents.length) return null;
                if (course.sections.length > 1 || occurrence) {
                    return (
                        <Section
                            key={`${section._id}-${index}`}
                            defaultExpanded={true}
                            sectionName={section.name}
                            sectionProgress={lessonProgress}
                            section={section}
                            lessonProgress={lessonProgress}
                        >
                            <div className={styles.lessons_list}>{renderLessonLink(section)}</div>
                        </Section>
                    );
                } else {
                    return (
                        <>
                            <div className={styles.lessons_list}>{renderLessonLink(section)}</div>
                        </>
                    );
                }
            })}
        </Pane>
    );
};

export default CourseSections;
